import { createContext, useContext, useState, ReactNode } from 'react'

export interface OnboardingData {
  // Company details
  companyName: string
  industry?: string
  timezone: string
  
  // Location details
  locationName: string
  locationType: 'physical' | 'remote'
  address?: string
  coordinates?: { lat: number; lng: number }
  geofence?: {
    type: 'circle' | 'polygon'
    data: any
  }
  
  // Constraint settings
  requireGeo: boolean
  requireQR: boolean
  requirePhoto: boolean
  requireWifi: boolean
  timeWindows?: Array<{
    daysOfWeek: number[]
    startTime: string
    endTime: string
  }>
  
  // Invitations
  invitations: Array<{
    contact: string
    type: 'email' | 'phone'
  }>
}

interface OnboardingContextType {
  data: OnboardingData
  currentStep: number
  totalSteps: number
  updateData: (updates: Partial<OnboardingData>) => void
  nextStep: () => void
  previousStep: () => void
  resetOnboarding: () => void
}

const defaultData: OnboardingData = {
  companyName: '',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  locationName: '',
  locationType: 'physical',
  requireGeo: false,
  requireQR: false,
  requirePhoto: false,
  requireWifi: false,
  invitations: []
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined)

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<OnboardingData>(defaultData)
  const [currentStep, setCurrentStep] = useState(0)
  const totalSteps = 6 // Choose path, Company, Location, Constraints, Review, Invitations
  
  const updateData = (updates: Partial<OnboardingData>) => {
    setData(prev => ({ ...prev, ...updates }))
  }
  
  const nextStep = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(prev => prev + 1)
    }
  }
  
  const previousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1)
    }
  }
  
  const resetOnboarding = () => {
    setData(defaultData)
    setCurrentStep(0)
  }
  
  return (
    <OnboardingContext.Provider
      value={{
        data,
        currentStep,
        totalSteps,
        updateData,
        nextStep,
        previousStep,
        resetOnboarding
      }}
    >
      {children}
    </OnboardingContext.Provider>
  )
}

export function useOnboarding() {
  const context = useContext(OnboardingContext)
  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider')
  }
  return context
}